import React from "react";
import constant from "../../../../constants";
import { FormInput, FormSelect } from "./FormField";
import { ExpireAtInput } from "./ExpireAtInput";
import { formatThaiDate } from "../utils";
import { validate } from "../../../../util";
import { FaTimes } from "react-icons/fa";
import { CopyButton } from "./CopyButton";

export const OrderGeneralForm = ({
  orderInfo,
  setOrderInfo,
  updateValue,
  loading,
  configs,
  toast,
}) => {
  const getStatusSelectClass = (status) => {
    switch (status) {
      case constant.statuses.APPROVE:
        return "bg-[rgb(59,176,101)] text-white font-bold border-[rgb(59,176,101)]";
      case constant.statuses.PAID:
        return "bg-[#4DA1A9] text-white font-bold border-[#4DA1A9]";
      case constant.statuses.PENDING:
        return "bg-[#FAB12F] text-slate-900 font-bold border-[#FAB12F]";
      case constant.statuses.CANCEL:
        return "bg-[#c15b78] text-white font-bold border-[#c15b78]";
      default:
        return "bg-white text-slate-800";
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Basic & Transaction Fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* สถานะ */}
        <FormSelect
          label="สถานะ"
          disabled={orderInfo.status === constant.statuses.CANCEL || loading}
          selectClassName={getStatusSelectClass(orderInfo?.status)}
          value={orderInfo?.status || ""}
          onChange={(e) => {
            if (
              e.target.value === constant.statuses.APPROVE &&
              !orderInfo?.paymentChannel
            ) {
              toast({
                className: "bg-rose-600 text-white font-semibold",
                title: (
                  <div className="flex gap-2 items-center">
                    <FaTimes /> กรุณาเลือกช่องทางการชำระเงินก่อนอนุมัติ
                  </div>
                ),
              });
              return;
            }
            updateValue("status", e.target.value);
          }}
        >
          <option value={constant.statuses.APPROVE} className="bg-white text-slate-800">
            {constant.statusesTranslate.approved}
          </option>
          <option value={constant.statuses.PAID} className="bg-white text-slate-800">
            {constant.statusesTranslate.paid}
          </option>
          <option value={constant.statuses.PENDING} className="bg-white text-slate-800">
            รอชำระ
          </option>
          <option value={constant.statuses.CANCEL} className="bg-white text-slate-800">
            {constant.statusesTranslate.canceled}
          </option>
        </FormSelect>

        {/* เวลาสร้างออเดอร์ */}
        <FormInput
          label="เวลาสร้างออเดอร์"
          disabled={true}
          value={formatThaiDate(orderInfo?.createdAt)}
        />

        {/* เวลาหมดอายุ */}
        <ExpireAtInput
          expireAt={orderInfo?.expireAt}
          onSave={updateValue}
          loading={loading}
        />

        {/* รหัสคำสั่งซื้อ */}
        <FormInput
          label="รหัสคำสั่งซื้อ"
          disabled={true}
          value={orderInfo?.shortId || ""}
          suffix={<CopyButton text={orderInfo?.shortId} />}
        />

        {/* ช่องทางการสั่งซื้อ */}
        <FormSelect
          label="ช่องทางการสั่งซื้อ"
          value={orderInfo?.deliverChannel || ""}
          disabled={loading}
          onChange={(e) => updateValue("deliverChannel", e.target.value)}
        >
          <option value="online">1. เช่าพื้นที่พร้อมบริการฯ</option>
          <option value="mail">2. ส่งไปรษณีย์</option>
          <option value="offline">3. 80 บาทมารับเอง</option>
        </FormSelect>

        {/* วิธีการชำระเงินหลัก */}
        <FormSelect
          label="วิธีการชำระเงินหลัก"
          value={
            orderInfo?.paymentMethod ||
            (orderInfo?.paidCurrency === "LAK"
              ? "LAO_BANK"
              : orderInfo?.paidCurrency === "CNY"
                ? "ALIPAY"
                : "TH_BANK")
          }
          disabled={loading}
          selectClassName="text-blue-700 font-bold"
          onChange={(e) => updateValue("paymentMethod", e.target.value)}
        >
          <option value="TH_BANK">1. ธนาคารไทย (THB)</option>
          <option value="LAO_BANK">2. ธนาคาร สปป.ลาว (LAK)</option>
          <option value="ALIPAY">3. Alipay (CNY)</option>
        </FormSelect>

        {/* ช่องทางการชำระเงิน */}
        <FormSelect
          label="ช่องทางการชำระเงิน"
          value={orderInfo?.paymentChannel || ""}
          disabled={loading}
          onChange={(e) => updateValue("paymentChannel", e.target.value)}
        >
          <option value="">-</option>
          <option value="qr">QR</option>
          <option value="bank">โอนเงิน</option>
          <option value="alipay">Alipay</option>
        </FormSelect>

        {/* จำนวนสลาก */}
        <FormInput
          label="จำนวนสลาก"
          disabled={true}
          value={orderInfo?.size || ""}
        />

        {/* ราคารวม (บาท) */}
        <FormInput
          label="ราคารวม (บาท)"
          type="number"
          disabled={loading}
          value={orderInfo?.totalPrice || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, totalPrice: e.target.value }))
          }
          onBlur={(e) => updateValue("totalPrice", e.target.value)}
        />

        {/* ราคารวม (หยวน) */}
        <FormInput
          label="ราคารวม (หยวน)"
          type="number"
          disabled={loading}
          value={orderInfo?.totalPrice_yuan || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, totalPrice_yuan: e.target.value }))
          }
          onBlur={(e) => updateValue("totalPrice_yuan", e.target.value)}
        />

        {/* ราคารวมกรณีเงินกีบ (LAK) */}
        {orderInfo?.paidCurrency === "LAK" && (
          <>
            <FormInput
              label="ราคารวม (กีบ)"
              disabled={loading}
              value={orderInfo?.totalPrice_lak?.toLocaleString() || ""}
              onChange={(e) => {
                if (validate.digitOnly.test(e.target.value)) {
                  setOrderInfo((prev) => ({
                    ...prev,
                    totalPrice_lak: e.target.value,
                  }));
                }
              }}
              onBlur={(e) => updateValue("totalPrice_lak", e.target.value)}
            />
            <FormInput
              label="ราคารวม (หยวน - คำนวณ)"
              disabled={true}
              value={
                configs?.currency?.cny
                  ? (
                    Math.ceil(
                      (orderInfo?.totalPrice / configs.currency.cny) * 100
                    ) / 100
                  ).toFixed(2)
                  : ""
              }
            />
          </>
        )}

        {/* เหตุผลในการเปลี่ยนราคา */}
        <FormInput
          label="เหตุผลในการเปลี่ยนราคา"
          disabled={loading}
          value={orderInfo?.reasonChangePrice || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({
              ...prev,
              reasonChangePrice: e.target.value,
            }))
          }
          onBlur={(e) => updateValue("reasonChangePrice", e.target.value)}
        />

        {/* ชื่อผู้ซื้อ */}
        <FormInput
          label="ชื่อผู้ซื้อ"
          disabled={loading}
          value={orderInfo?.firstName || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, firstName: e.target.value }))
          }
          onBlur={(e) => updateValue("firstName", e.target.value)}
        />

        {/* นามสกุลผู้ซื้อ */}
        <FormInput
          label="นามสกุลผู้ซื้อ"
          disabled={loading}
          value={orderInfo?.lastName || ""}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, lastName: e.target.value }))
          }
          onBlur={(e) => updateValue("lastName", e.target.value)}
        />

        {/* เบอร์โทรศัพท์ผู้ซื้อ */}
        <FormInput
          label="เบอร์โทรศัพท์ผู้ซื้อ"
          disabled={loading}
          value={orderInfo?.phone || ""}
          suffix={<CopyButton text={orderInfo?.phone} />}
          onChange={(e) =>
            setOrderInfo((prev) => ({ ...prev, phone: e.target.value }))
          }
          onBlur={(e) => updateValue("phone", e.target.value)}
        />
      </div>
    </div>
  );
};
